import { lazy, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Row } from 'react-table';

import FilterDefault from 'app/components/Filter/FilterDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { QUERIES, SORT_BY_NAME_TABLE_QUERY } from 'app/components/Table/core/constants';
import { stringifyRequestQuery } from 'app/components/Table/core/helpers';
import { TYPE_MODAL } from 'app/components/Table/core/types';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useAuth } from 'app/modules/auth';
import RackFilter from 'app/modules/inventory/racks/components/RackFilter';
import {
  checkParamSynced,
  useGetQueryParams,
  useSyncQueryToState,
} from 'app/modules/query-state/hooks';
import { getWards } from 'app/modules/system-settings/locations/wards/core/requests';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { useNavigateWithSendPath } from 'app/helpers/utils';
import { usePolicyInUser } from 'app/hooks/policy';
import { SocketClient } from 'app/hooks/socket';
import isNil from 'lodash/isNil';

import { HIDDEN_BUTTON_ADD } from 'app/constants/environments';
import { PATH } from 'app/constants/path';

import { racksColumns } from './core/columns';
import { deleteRack, getRacks2 } from './core/requests';
import { Rack, RackFilterParams } from './core/type';
import FormRackModal from './modal/FormRack.modal';
import { useRackStore } from './store';
import { SuspensedView } from 'app/routing/PrivateRoutes';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UnableRemoveRackModal = lazy(() => import('./components/UnableRemoveRackModal'));

const RackManagementPage = () => {
  useSyncQueryToState(window.location.pathname, SORT_BY_NAME_TABLE_QUERY);

  const currentUser = useAuth().currentUser;
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);
  const { synced, curParams } = useGetQueryParams<RackFilterParams>();
  const isSynced = checkParamSynced(synced);
  const [rack, setRack] = useState<Rack | null>(null);
  const [isOpenUnableRemoveRackModal, setIsOpenUnableRemoveRackModal] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigateWithSendPath();

  const [wards, setWards] = useState<Ward[]>([]);
  const [isShowFilter, setIsShowFilter] = useState(false);

  useEffect(() => {
    if (isSynced) queryClient.invalidateQueries(QUERIES.MAIL_TRACKING_LIST);
  }, [isSynced, JSON.stringify(curParams)]);

  useEffect(() => {
    getData();
    SocketClient.getAllTablets();

    SocketClient.onTabletInAllWards((tablets) => {
      useRackStore.getState().setState({ tablets });
    });

    return () => {
      SocketClient.offTablets();
      useRackStore.getState().clearState();
    };
  }, []);

  const getData = async () => {
    const dataResWards = await getWards(
      stringifyRequestQuery({
        page: 1,
        limit: 100,
      }),
    );
    const newResWards = dataResWards.data?.items || [];
    newResWards.unshift({
      createdAt: '',
      id: 0,
      name: 'None',
      updatedAt: '',
    });
    setWards(newResWards);
  };

  const renderModalForm = (originalData: Rack, _typeModal: TYPE_MODAL, _isView?: boolean) => {
    const mappingFormat =
      _typeModal === TYPE_MODAL.ADD
        ? originalData
        : {
            ...originalData,
            isCustom: originalData?.['is_custom'],
            binCount: originalData?.['bin_count'],
          };
    return <FormRackModal wards={wards} itemRack={mappingFormat} />;
  };
  const onClickBodyTable = (row: Row) => () => {
    const newRack = row.original as Rack;
    if (newRack.id) {
      navigate(PATH.RACK_DETAIL.replace(':rackId', String(newRack.id)));
    }
  };

  const handleClickFilter = (type: 'on' | 'off') => setIsShowFilter(type === 'on');

  const hasActiveFilters = Object.keys(curParams).some(
    (key) => ['wardIds', 'is_custom'].includes(key) && !isNil(curParams[key]),
  );

  const handleGetDataRack = (item: Rack) => setRack(item);

  const handleOpenUnableRemoveRackModal = (element: Rack) => () => {
    setRack(element);
    setIsOpenUnableRemoveRackModal(true);
  };
  const handleCloseUnableRemoveRackModal = () => {
    setIsOpenUnableRemoveRackModal(false);
    setRack(null);
  };

  const columns = useMemo(() => racksColumns(handleOpenUnableRemoveRackModal), [rack]);

  return (
    <RootContentPage title="Racks">
      <TableComponent
        getList={getRacks2}
        nameQuery={QUERIES.RACK_LIST}
        originColumns={columns}
        listHeaderPosition="left"
        titleModal="rack"
        placeholderSearch="Search with rack name and location"
        nameBtnAdd={!HIDDEN_BUTTON_ADD ? 'Add rack' : ''}
        moreQuery={isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''}
        isBorder={false}
        deleteById={deleteRack}
        ModalForm={renderModalForm}
        onClickBodyTable={onClickBodyTable}
        syncParamUrl
        spacing="xxl"
        customListHeader={
          <FilterDefault
            visible={isShowFilter}
            onClickFilter={handleClickFilter}
            isFilter={hasActiveFilters}
            content={
              isShowFilter && (
                <RackFilter
                  wards={wards}
                  openFilter={isShowFilter}
                  setIsOpenFilter={setIsShowFilter}
                />
              )
            }
          />
        }
        getDataItem={handleGetDataRack}
      />

      <SuspensedView>
        {isOpenUnableRemoveRackModal && (
          <UnableRemoveRackModal
            rack={rack}
            isOpen={isOpenUnableRemoveRackModal}
            onClose={handleCloseUnableRemoveRackModal}
          />
        )}
      </SuspensedView>
    </RootContentPage>
  );
};

export default RackManagementPage;
